import React from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect
} from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/layout/Navbar';
import PublicFooter from './components/layout/PublicFooter';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import TestDashboard2 from './pages/TestDashboard2';
import EventDetail from './pages/EventDetail';
import CreateEvent from './pages/CreateEvent';
import Applications from './pages/Applications';
import VendorProfile from './pages/VendorProfile';
import PublicVendorProfile from './pages/PublicVendorProfile';
import VendorDirectory from './pages/VendorDirectory';
import OrganizerProfile from './pages/OrganizerProfile';
import NotFound from './pages/NotFound';
import FindEvents from './pages/FindEvents';
import CreateOrganizerProfile from './pages/CreateOrganizerProfile';
import CreateVendorProfile from './pages/CreateVendorProfile';
import MyEvents from './pages/MyEvents';
import EventApplications from './pages/EventApplications';
import EventCRM from './pages/EventCRM';
import Notifications from './pages/Notifications';
import PaymentInfo from './pages/PaymentInfo';
import LandingPage from './pages/LandingPage';
import ForVendors from './pages/ForVendors';
import ForOrganizers from './pages/ForOrganizers';
import AboutUs from './pages/AboutUs';
import Contact from './pages/Contact';
import EventManagement from './pages/EventManagement';
import EventStats from './pages/EventStats';
import VerifyEmail from './pages/VerifyEmail';
import EmailVerificationRequired from './pages/EmailVerificationRequired';
import Messages from './pages/Messages';

// Components
import ProfileRequiredRoute from './components/ProfileRequiredRoute';
import FloatingBugReportButton from './components/FloatingBugReportButton';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    secondary: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
  },
});

const queryClient = new QueryClient();

// Protected Route component - requires authentication AND email verification
const PrivateRoute = ({ children, ...rest }) => {
  const { currentUser } = useAuth();
  
  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (!currentUser) {
          return (
            <Redirect
              to={{
                pathname: "/login",
                state: { from: location }
              }}
            />
          );
        }
        
        // Check if email is verified
        if (currentUser.email_verified === false) {
          return <EmailVerificationRequired />;
        }
        
        return children;
      }}
    />
  );
};

// Public Route component (redirects to dashboard if already logged in)
const PublicRoute = ({ children, ...rest }) => {
  const { currentUser } = useAuth();
  
  return (
    <Route
      {...rest}
      render={({ location }) =>
        currentUser ? (
          <Redirect
            to={{
              pathname: "/dashboard",
              state: { from: location }
            }}
          />
        ) : (
          children
        )
      }
    />
  );
};

// Vendor-only Applications route wrapper
const VendorApplicationsRoute = () => {
  const { currentUser } = useAuth();
  if (!currentUser) return null; // or a spinner if desired
  return Number(currentUser.role_id) === 1 ? <Applications /> : <Redirect to="/dashboard" />;
};

// Organizer-only route wrapper
const OrganizerRoute = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser) return <Redirect to="/login" />;
  return Number(currentUser.role_id) === 2 ? children : <Redirect to="/dashboard" />;
};

// Vendor-only route wrapper
const VendorRoute = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser) return <Redirect to="/login" />;
  return Number(currentUser.role_id) === 1 ? children : <Redirect to="/dashboard" />;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <div className="App">
              {/* Only show Navbar and Footer for protected routes */}
              <Switch>
                {/* Public routes without Navbar and Footer */}
                <Route exact path="/" component={LandingPage} />
                <Route path="/about" component={AboutUs} />
                <Route path="/contact" component={Contact} />
                <Route path="/for-vendors" component={ForVendors} />
                <Route path="/for-organizers" component={ForOrganizers} />
                <Route path="/login" component={Login} />
                {/* Register route - allow logged-in users to see success message */}
                <Route 
                  path="/register" 
                  render={() => <Register />}
                />
                <Route path="/verify-email" component={VerifyEmail} />
                <Route path="/email-verification-required" component={EmailVerificationRequired} />

                {/* Protected routes with Navbar and Footer */}
                <Route
                  path="/dashboard"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <Navbar />
                        <Dashboard />
                        <PublicFooter />
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/testdashboard2"
                  render={() => (
                    <PrivateRoute>
                      <Navbar />
                      <TestDashboard2 />
                      <PublicFooter />
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/events"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <Navbar />
                        <FindEvents />
                        <PublicFooter />
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/applications"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <Navbar />
                        <VendorApplicationsRoute />
                        <PublicFooter />
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/notifications"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <Navbar />
                        <Notifications />
                        <PublicFooter />
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/messages"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <Navbar />
                        <Messages />
                        <PublicFooter />
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/applications/:applicationId/payment"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <VendorRoute>
                          <Navbar />
                          <PaymentInfo />
                          <PublicFooter />
                        </VendorRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/profile"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <VendorRoute>
                          <Navbar />
                          <VendorProfile />
                          <PublicFooter />
                        </VendorRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  exact
                  path="/event/:eventId"
                  render={({ match }) => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <Navbar />
                        <EventDetail />
                        <PublicFooter />
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/event/:eventId/edit"
                  render={({ match }) => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <OrganizerRoute>
                          <Navbar />
                          <EventManagement />
                          <PublicFooter />
                        </OrganizerRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/event/:eventId/applications"
                  render={({ match }) => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <OrganizerRoute>
                          <Navbar />
                          <EventCRM />
                          <PublicFooter />
                        </OrganizerRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/event/:eventId/stats"
                  render={({ match }) => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <OrganizerRoute>
                          <Navbar />
                          <EventStats />
                          <PublicFooter />
                        </OrganizerRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/create-event"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <OrganizerRoute>
                          <Navbar />
                          <CreateEvent />
                          <PublicFooter />
                        </OrganizerRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/my-events"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <OrganizerRoute>
                          <Navbar />
                          <MyEvents />
                          <PublicFooter />
                        </OrganizerRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/vendor-profile"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <VendorRoute>
                          <Navbar />
                          <VendorProfile />
                          <PublicFooter />
                        </VendorRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  exact
                  path="/vendors"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <OrganizerRoute>
                          <Navbar />
                          <VendorDirectory />
                          <PublicFooter />
                        </OrganizerRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/vendors/:vendorId"
                  render={({ match }) => (
                    <>
                      <Navbar />
                      <PublicVendorProfile />
                      <PublicFooter />
                    </>
                  )}
                />
                <Route
                  path="/organizers/:organizerId"
                  render={({ match }) => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <Navbar />
                        <OrganizerProfile />
                        <PublicFooter />
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/organizer-profile"
                  render={() => (
                    <PrivateRoute>
                      <ProfileRequiredRoute>
                        <OrganizerRoute>
                          <Navbar />
                          <OrganizerProfile />
                          <PublicFooter />
                        </OrganizerRoute>
                      </ProfileRequiredRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/create-organizer-profile"
                  render={() => (
                    <PrivateRoute>
                      <OrganizerRoute>
                        <Navbar />
                        <CreateOrganizerProfile />
                        <PublicFooter />
                      </OrganizerRoute>
                    </PrivateRoute>
                  )}
                />
                <Route
                  path="/create-vendor-profile"
                  render={() => (
                    <PrivateRoute>
                      <VendorRoute>
                        <Navbar />
                        <CreateVendorProfile />
                        <PublicFooter />
                      </VendorRoute>
                    </PrivateRoute>
                  )}
                />
                <Route path="*" component={NotFound} />
              </Switch>
              {/* Floating Bug Report Button - appears on all pages */}
              <FloatingBugReportButton />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;